import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, ChevronDown, ChevronRight, Copy, Download, Eye, Folder, FolderArchive, FolderInput,
  FolderOpen, FolderPlus, Image as ImageIcon, Info, Lock, Mail, Paperclip, Pencil, PenLine, PenTool, Share2,
  ShieldAlert, ShieldOff, Trash2, Unlink, Upload, X,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { MediaLightbox } from "@/features/chat/components/MediaLightbox";
import { imageSeries, type ImageSeries } from "@/features/doc_editor/imagePreview";
import { filesTabOpenAction, type FilesTabOpenAction } from "@/features/doc_editor/types";
import { downloadProjectFolderZip } from "../api";
import { jestRysunkiem, otworzRysunek } from "../cadViewer";
import {
  qk, useProjectFolders, useConnectionFileMutations, useCreateProjectFolder, useDeleteProjectFolder,
  useDetachWypisFromFolder, useRenameProjectFolder, useSetFolderConfidentiality,
  useShareableOrders, useShareToOrder, useTransferProjectFiles, useUnshareFromOrder,
} from "../hooks";
import type {
  FolderFileEntry, FolderNode, PowerProject, ProjectFileShareRow, ShareMode, SharedOrderRef,
} from "../types";
import { apiErr, fmtDateTime } from "../utils";

// Cel okna udostępniania: wszystkie niepoufne pliki projektu, jeden folder albo jeden plik.
type ShareTarget =
  | { kind: "all" }
  | { kind: "folder"; slug: string; label: string }
  | { kind: "file"; managedFileId: string; filename: string };

// Dlaczego zamówienie widzi dany folder/plik — tryb rozstrzyga, czy da się cofnąć punktowo.
const SHARE_MODE_LABEL: Record<ShareMode, string> = {
  all: "wszystkie niepoufne",
  folder: "folder udostępniony wprost",
  inherited: "przez folder nadrzędny",
  file: "pojedynczy plik",
};

// Podpowiedź pod znacznikiem „udostępnione": kto to widzi i z jakiego powodu.
function shareTitle(refs: SharedOrderRef[]): string {
  return [
    "Udostępnione w zamówieniach:",
    ...refs.map(
      (r) => `• ${r.order_number}${r.contractor_name ? ` · ${r.contractor_name}` : ""}`
        + ` (${SHARE_MODE_LABEL[r.mode]})`,
    ),
  ].join("\n");
}

// Operacja kopiuj/przenieś: pojedynczy plik (kopia po managed_file_id, przeniesienie po
// connection_file_id) albo cała zawartość folderu. `fromSlug` wyklucza folder źródłowy
// z listy celów.
type TransferSource =
  | { kind: "file"; filename: string; fromSlug: string; managedFileId: string; connectionFileId: string | null }
  | { kind: "folder"; slug: string; label: string };
type TransferTarget = { operation: "copy" | "move"; source: TransferSource };

// Wszystkie foldery nieautomatyczne (płaskie liście) — jedyne dozwolone cele operacji.
function manualFolders(nodes: FolderNode[], acc: FolderNode[] = []): FolderNode[] {
  for (const n of nodes) {
    if (n.kind === "manual") acc.push(n);
    manualFolders(n.children, acc);
  }
  return acc;
}

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// Flatten the tree into the leaf nodes (the only folders that hold files / accept uploads).
function collectLeaves(nodes: FolderNode[], acc: FolderNode[] = []): FolderNode[] {
  for (const n of nodes) {
    if (n.children.length === 0) acc.push(n);
    else collectLeaves(n.children, acc);
  }
  return acc;
}

function findNode(nodes: FolderNode[], slug: string): FolderNode | null {
  for (const n of nodes) {
    if (n.slug === slug) return n;
    const hit = findNode(n.children, slug);
    if (hit) return hit;
  }
  return null;
}

// Pierwsze wystąpienie pliku w drzewie — kopie w kilku folderach wskazują ten sam
// ManagedFile, a backend liczy znaczniki udostępnień właśnie po nim.
function findEntry(nodes: FolderNode[], managedFileId: string): FolderFileEntry | null {
  for (const n of nodes) {
    const hit = n.entries.find((e) => e.managed_file_id === managedFileId);
    if (hit) return hit;
    const deep = findEntry(n.children, managedFileId);
    if (deep) return deep;
  }
  return null;
}

// ── left pane: recursive folder tree ─────────────────────────────────────────
function TreeNode({
  node, depth, selectedSlug, onSelect, onDownloadZip, zipBusy,
  canManageConfidential, onToggleConfidential, confidentialBusy,
  canShareToOrder, onShareToOrder,
  canManageFolders, onRenameFolder, onDeleteFolder,
}: {
  node: FolderNode; depth: number; selectedSlug: string; onSelect: (slug: string) => void;
  onDownloadZip: (slug: string) => void; zipBusy: boolean;
  canManageConfidential: boolean;
  onToggleConfidential: (slug: string, isConfidential: boolean) => void;
  confidentialBusy: boolean;
  canShareToOrder: boolean;
  onShareToOrder: (target: ShareTarget) => void;
  // Zmiana nazwy / usunięcie — tylko foldery własne (is_custom), ta sama bramka co tworzenie.
  canManageFolders: boolean;
  onRenameFolder: (node: FolderNode) => void;
  onDeleteFolder: (node: FolderNode) => void;
}) {
  const isContainer = node.children.length > 0;
  const [open, setOpen] = useState(true);
  const selected = node.slug === selectedSlug;
  // Zamówienia, które ten folder DZIŚ widzą — znacznik zastępuje ikonę „udostępnij"
  // (ta sama akcja, tylko widoczna bez najeżdżania i z liczbą zamówień).
  const sharedRefs = node.shared_orders ?? [];

  return (
    <div>
      <div
        className={`group flex items-center py-1.5 pr-2 text-sm transition-colors ${
          selected
            ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium"
            : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)]"
        }`}
        style={{ paddingLeft: `${8 + depth * 14}px` }}
      >
        <button
          type="button"
          onClick={() => (isContainer ? setOpen((o) => !o) : onSelect(node.slug))}
          className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
        >
          {isContainer ? (
            open ? <ChevronDown size={13} className="shrink-0" /> : <ChevronRight size={13} className="shrink-0" />
          ) : (
            <span className="w-[13px] shrink-0" />
          )}
          {selected ? <FolderOpen size={14} className="shrink-0" /> : <Folder size={14} className="shrink-0" />}
          <span className="truncate flex-1" title={node.label}>{node.label}</span>
          {node.is_confidential && (
            <ShieldAlert
              size={12}
              className={`shrink-0 ${node.confidential_locked ? "text-[var(--color-primary)]" : "text-amber-600"}`}
              aria-label={
                node.confidential_locked
                  ? "Folder zastrzeżony na stałe"
                  : node.confidential_own == null
                    ? "Folder poufny (dziedziczone)"
                    : "Folder poufny"
              }
            />
          )}
          {node.read_only && <Lock size={11} className="shrink-0 text-[var(--color-text-muted)]" />}
          {node.file_count > 0 && (
            <span className="shrink-0 text-[10px] font-mono px-1 rounded bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]">
              {node.file_count}
            </span>
          )}
        </button>
        {canManageFolders && node.is_custom && (
          <>
            <button
              type="button"
              className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              title={`Zmień nazwę folderu „${node.label}"`}
              onClick={() => onRenameFolder(node)}
            >
              <Pencil size={13} />
            </button>
            <button
              type="button"
              className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              title={`Usuń folder „${node.label}"`}
              onClick={() => onDeleteFolder(node)}
            >
              <Trash2 size={13} />
            </button>
          </>
        )}
        {canShareToOrder && !node.is_confidential && (
          <button
            type="button"
            className={`shrink-0 p-0.5 ml-1.5 inline-flex items-center gap-0.5 transition-opacity ${
              sharedRefs.length > 0
                ? "text-[var(--color-primary)] opacity-100"
                : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100"
            }`}
            title={
              sharedRefs.length > 0
                ? shareTitle(sharedRefs)
                : `Udostępnij folder „${node.label}" w zleceniu`
            }
            onClick={() => onShareToOrder({ kind: "folder", slug: node.slug, label: node.label })}
          >
            <Share2 size={13} />
            {sharedRefs.length > 0 && (
              <span className="text-[10px] font-mono leading-none">{sharedRefs.length}</span>
            )}
          </button>
        )}
        {/* Folder zastrzeżony z definicji (UMOWA PODPISANA) — poufności nie da się zdjąć,
            więc zamiast martwego przełącznika pokazujemy kłódkę z wyjaśnieniem. */}
        {node.confidential_locked && (
          <span
            className="shrink-0 p-0.5 ml-1.5 text-[var(--color-primary)]"
            title={
              node.move_out_allowed
                ? `Folder „${node.label}" jest zastrzeżony na stałe — widzą go wyłącznie kierownicy, dyrektorzy i CEO; nie podlega udostępnieniu. Pliki można przenieść do innego folderu — pozostają wtedy poufne.`
                : `Folder „${node.label}" jest zastrzeżony na stałe — widzą go wyłącznie kierownicy, dyrektorzy i CEO. Poufności nie można zdjąć, folderu nie da się udostępnić w zleceniu, a plików wynieść do jawnego folderu.`
            }
          >
            <Lock size={13} />
          </span>
        )}
        {canManageConfidential && !node.confidential_locked && (
          <button
            type="button"
            className={`shrink-0 p-0.5 ml-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 transition-opacity ${
              node.is_confidential
                ? "text-amber-600 hover:text-[var(--color-text-primary)] opacity-100"
                : "text-[var(--color-text-muted)] hover:text-amber-600"
            }`}
            title={
              node.is_confidential
                ? `Odpoufnij folder „${node.label}" (jawne nadpisanie — obejmie też podfoldery i pliki, nawet pod poufnym rodzicem)`
                : `Oznacz folder „${node.label}" jako poufny (widoczny tylko dla koordynatora, dyrektora i CEO; obejmuje podfoldery i pliki)`
            }
            disabled={confidentialBusy}
            onClick={() => onToggleConfidential(node.slug, !node.is_confidential)}
          >
            {node.is_confidential ? <ShieldOff size={13} /> : <ShieldAlert size={13} />}
          </button>
        )}
        {node.file_count > 0 && (
          <button
            type="button"
            className="shrink-0 p-0.5 ml-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 transition-opacity"
            title={`Pobierz „${node.label}" jako ZIP`}
            disabled={zipBusy}
            onClick={() => onDownloadZip(node.slug)}
          >
            <Download size={13} />
          </button>
        )}
      </div>
      {isContainer && open && node.children.map((c) => (
        <TreeNode
          key={c.slug}
          node={c}
          depth={depth + 1}
          selectedSlug={selectedSlug}
          onSelect={onSelect}
          onDownloadZip={onDownloadZip}
          zipBusy={zipBusy}
          canManageConfidential={canManageConfidential}
          onToggleConfidential={onToggleConfidential}
          confidentialBusy={confidentialBusy}
          canShareToOrder={canShareToOrder}
          onShareToOrder={onShareToOrder}
          canManageFolders={canManageFolders}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
        />
      ))}
    </div>
  );
}

// Czy treść pliku da się ZAPISAĆ z edytora w przeglądarce: tylko wiersz ręczny (ConnectionFile),
// a nie wpis z projekcji (trasa, wniosek, decyzja… — connection_file_id puste) ani lustro
// korespondencji (dzieli plik ze skanem w rejestrze). Backend (slot `project_file`) rozstrzyga
// ostatecznie — tu tylko zapowiadamy tryb w podpowiedzi.
function canEditContent(entry: FolderFileEntry): boolean {
  return !!entry.connection_file_id && !entry.from_correspondence;
}

// Id slotu `project_file`: wiersz ręczny po swoim id, wpis z projekcji po parze
// „<connection>:<plik>" (backend sprawdza, że plik leży w drzewie TEGO projektu).
function editorSlotId(connectionId: string, entry: FolderFileEntry): string {
  return entry.connection_file_id ?? `${connectionId}:${entry.managed_file_id}`;
}

// ── right pane: file rows ─────────────────────────────────────────────────────
function EntryRow({
  entry, onDelete, onSaveDescription, saving, onSaveFilename, savingName,
  onShareToOrder, onToggleConfidential, onCopy, onMove, onDetachWypis, openAction, onOpen,
}: {
  entry: FolderFileEntry;
  onDelete: () => void;
  /** Jak otworzyć: „edit" = Collabora z prawem zapisu, „view" = podgląd w Collaborze (PDF,
   *  projekcje), „image" = podgląd zdjęcia jak w GG. */
  openAction?: FilesTabOpenAction;
  onOpen?: () => void;
  onSaveDescription: (description: string, close: () => void) => void;
  saving: boolean;
  // Zmiana nazwy pliku (tylko wpisy ręczne) — kopie w innych folderach wskazują ten sam
  // plik, więc nazwa zmienia się wszędzie.
  onSaveFilename: (filename: string, close: () => void) => void;
  savingName: boolean;
  // Ustawione tylko dla uprawnionych (kierownik przedsięwzięcia/dyrektor/CEO) i tylko na
  // jawnych plikach — włącza „udostępnij w zleceniu" na wierszu pliku.
  onShareToOrder?: () => void;
  // Ustawione dla koordynatora/dyrektora/CEO — jawne nadpisanie poufności TEGO pliku
  // (działa też „w dół" względem folderu: plik można odpoufnić mimo poufnego folderu).
  onToggleConfidential?: (isConfidential: boolean) => void;
  // Kopiowanie do folderu nieautomatycznego — dozwolone z każdego folderu.
  onCopy?: () => void;
  // Przenoszenie — tylko pliki ręczne leżące w folderze nieautomatycznym.
  onMove?: () => void;
  // Odpięcie wypisu od działek (wpis `parcel_wypis` w folderze WYPISY; kierownik/dyrektor/CEO).
  // Kasuje tylko powiązanie — plik zostaje w projekcie; potwierdzenie w modalu.
  onDetachWypis?: () => void;
}) {
  const editable = entry.connection_file_id != null;
  // Zamówienia, które ten plik dziś widzą (znacznik + podpowiedź przy ikonie udostępniania).
  const sharedRefs = entry.shared_orders ?? [];
  // Lustro z dziennika korespondencji: opis można edytować (lokalny dla projektu), ale
  // nazwa jest współdzielona ze skanem w rejestrze, a usunięcie = odpięcie w widoku
  // korespondencji (adresat dekretacji albo dyrektor) — backend odrzuca oba.
  const manageable = editable && !entry.from_correspondence;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.source_label);
  const save = () => onSaveDescription(draft.trim(), () => setEditing(false));
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(entry.filename);
  // Otwieranie rysunku idzie przez bilet z backendu — krótka chwila czekania, w której
  // przycisk musi być zablokowany, żeby kliknięcie dwa razy nie otwarło dwóch kart.
  const [otwieranie, setOtwieranie] = useState(false);
  const saveName = () => {
    const next = nameDraft.trim();
    if (next) onSaveFilename(next, () => setEditingName(false));
  };

  return (
    <tr className="border-b border-[var(--color-border-muted)] last:border-0 hover:bg-[var(--color-bg-elevated)] transition-colors">
      <td className="py-3 pl-4 pr-2">
        <Paperclip size={14} className="text-[var(--color-text-muted)]" />
      </td>
      {/* w-full + max-w-0: the column fills the remaining table width, but long
          names/descriptions truncate instead of squeezing size/date/actions. */}
      <td className="py-3 pr-4 w-full max-w-0">
        {editingName ? (
          <div className="flex items-center gap-1.5">
            <input
              className="input-base text-xs py-1"
              value={nameDraft}
              autoFocus
              placeholder="Nazwa pliku"
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveName();
                if (e.key === "Escape") setEditingName(false);
              }}
            />
            <Button size="sm" loading={savingName} onClick={saveName}>Zapisz</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditingName(false)}>Anuluj</Button>
          </div>
        ) : (
          <p className="text-sm font-medium text-[var(--color-text-primary)] min-w-0 flex items-center gap-1.5" title={entry.filename}>
            {onOpen ? (
              <button
                type="button"
                className="min-w-0 inline-flex items-center gap-1.5 text-left hover:text-[var(--color-primary)] hover:underline"
                title={
                  openAction === "image" ? "Otwórz podgląd zdjęcia"
                    : openAction === "edit" ? "Otwórz do edycji w przeglądarce" : "Otwórz podgląd w przeglądarce"
                }
                onClick={onOpen}
              >
                {openAction === "image"
                  ? <ImageIcon size={12} className="shrink-0 text-[var(--color-text-muted)]" />
                  : openAction === "edit"
                    ? <PenLine size={12} className="shrink-0 text-[var(--color-text-muted)]" />
                    : <Eye size={12} className="shrink-0 text-[var(--color-text-muted)]" />}
                <span className="truncate">{entry.filename}</span>
              </button>
            ) : (
              <span className="truncate">{entry.filename}</span>
            )}
            {manageable && (
              <button
                type="button"
                className="p-0.5 shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Zmień nazwę pliku"
                onClick={() => { setNameDraft(entry.filename); setEditingName(true); }}
              >
                <Pencil size={12} />
              </button>
            )}
            {entry.from_correspondence && (
              <Mail
                size={12}
                className="shrink-0 text-[var(--color-text-muted)]"
                aria-label="Plik z dziennika korespondencji — odpiąć go od projektu może adresat dekretacji albo dyrektor w widoku korespondencji"
              />
            )}
            {entry.is_confidential && (
              <ShieldAlert
                size={12}
                className={`shrink-0 ${entry.confidential_locked ? "text-[var(--color-primary)]" : "text-amber-600"}`}
                aria-label={
                  entry.confidential_locked
                    ? "Plik w folderze zastrzeżonym na stałe"
                    : entry.confidential_own == null
                      ? "Plik poufny (dziedziczone z folderu)"
                      : "Plik poufny"
                }
              />
            )}
          </p>
        )}
        {editing ? (
          <div className="flex items-center gap-1.5 mt-1">
            <input
              className="input-base text-xs py-1"
              value={draft}
              autoFocus
              placeholder="Opis pliku"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <Button size="sm" loading={saving} onClick={save}>Zapisz</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Anuluj</Button>
          </div>
        ) : (
          entry.source_label && (
            <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 min-w-0 flex items-center gap-1.5">
              <span className="truncate" title={entry.source_label}>{entry.source_label}</span>
              {editable && (
                <button
                  type="button"
                  className="p-0.5 shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                  title="Edytuj opis"
                  onClick={() => { setDraft(entry.source_label); setEditing(true); }}
                >
                  <Pencil size={12} />
                </button>
              )}
            </p>
          )
        )}
      </td>
      <td className="py-3 pr-4 text-xs font-mono text-[var(--color-text-muted)] whitespace-nowrap">
        {fmtBytes(entry.size_bytes)}
      </td>
      <td className="py-3 pr-4 text-xs text-[var(--color-text-muted)] whitespace-nowrap">
        {entry.created_at ? fmtDateTime(entry.created_at) : "—"}
      </td>
      <td className="py-3 pr-3">
        <div className="flex items-center gap-1 justify-end">
          {entry.confidential_locked && (
            <span
              className="p-1.5 text-[var(--color-primary)]"
              title={
                entry.from_correspondence
                  ? "Plik leży w folderze zastrzeżonym na stałe — poufności nie można zdjąć ani udostępnić go w zleceniu. Przeniesiony do innego folderu pozostanie poufny do jawnego odpoufnienia."
                  : "Plik leży w folderze zastrzeżonym na stałe — poufności nie można zdjąć, pliku nie da się udostępnić w zleceniu ani przenieść do jawnego folderu."
              }
            >
              <Lock size={15} />
            </span>
          )}
          {onToggleConfidential && !entry.confidential_locked && (
            <button
              type="button"
              className={`p-1.5 transition-colors ${
                entry.is_confidential
                  ? "text-amber-600 hover:text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-muted)] hover:text-amber-600"
              }`}
              title={
                entry.is_confidential
                  ? "Odpoufnij plik (jawne nadpisanie — widoczny dla wszystkich, nawet gdy folder jest poufny)"
                  : "Oznacz plik jako poufny (widoczny tylko dla koordynatora, dyrektora i CEO)"
              }
              onClick={() => onToggleConfidential(!entry.is_confidential)}
            >
              {entry.is_confidential ? <ShieldOff size={15} /> : <ShieldAlert size={15} />}
            </button>
          )}
          {onShareToOrder && (
            <button
              type="button"
              className={`p-1.5 inline-flex items-center gap-0.5 transition-colors ${
                sharedRefs.length > 0
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              }`}
              title={
                sharedRefs.length > 0 ? shareTitle(sharedRefs) : "Udostępnij plik w zleceniu"
              }
              onClick={onShareToOrder}
            >
              <Share2 size={15} />
              {sharedRefs.length > 0 && (
                <span className="text-[10px] font-mono leading-none">{sharedRefs.length}</span>
              )}
            </button>
          )}
          {onCopy && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Kopiuj do folderu…"
              onClick={onCopy}
            >
              <Copy size={15} />
            </button>
          )}
          {onMove && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Przenieś do folderu…"
              onClick={onMove}
            >
              <FolderInput size={15} />
            </button>
          )}
          {jestRysunkiem(entry.filename) && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors disabled:opacity-40"
              title="Przejdź do rysunku — otwórz w przeglądarce"
              disabled={otwieranie}
              onClick={() => {
                setOtwieranie(true);
                otworzRysunek(entry.managed_file_id)
                  .catch((e) => alert(apiErr(e, "Nie udało się otworzyć rysunku.")))
                  .finally(() => setOtwieranie(false));
              }}
            >
              {otwieranie ? <Spinner size="sm" className="w-[15px] h-[15px]" /> : <PenTool size={15} />}
            </button>
          )}
          <a
            href={entry.url ?? "#"}
            download={entry.filename}
            target="_blank"
            rel="noreferrer"
            className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
            title="Pobierz"
          >
            <Download size={15} />
          </a>
          {onDetachWypis && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-red-500 transition-colors"
              title="Odepnij wypis od działek…"
              onClick={onDetachWypis}
            >
              <Unlink size={15} />
            </button>
          )}
          {manageable && (
            <button
              type="button"
              className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
              title="Usuń plik"
              onClick={onDelete}
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Potwierdzenie odpięcia wypisu od działek (folder WYPISY). Backend kasuje wyłącznie
// powiązanie (działki + mapa stron) — plik zostaje w projekcie i wraca do „Inne / bez
// folderu", właściciele odczytani z wypisu zostają. Modal zamiast confirm(), bo skutek
// (utrata mapy stron) jest nieodwracalny bez ponownego przypisania.
function DetachWypisModal({
  entry, busy, onClose, onConfirm,
}: {
  entry: FolderFileEntry; busy: boolean; onClose: () => void; onConfirm: () => void;
}) {
  const parcels = (entry.source_ref.extra?.parcels as string[] | undefined) ?? [];
  return (
    <Modal open onClose={onClose} title="Odpiąć wypis od działek?" size="sm">
      <div className="space-y-3 text-sm">
        <p>
          Plik <b>{entry.filename}</b> zostanie odpięty od {parcels.length === 1 ? "działki" : "działek"}
          {parcels.length > 0 && <>: <span className="font-mono">{parcels.join(", ")}</span></>}.
        </p>
        <ul className="list-disc pl-5 text-xs text-[var(--color-text-muted)] space-y-1">
          <li>Plik <b>nie zostanie usunięty</b> — wraca do folderu „Inne / bez folderu" i można go podpiąć ponownie w zakładce Działki.</li>
          <li>Znika mapa stron (która strona wypisu dotyczy której działki).</li>
          <li>Właściciele odczytani z tego wypisu zostają przy działkach.</li>
        </ul>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Anuluj</Button>
          <Button size="sm" variant="danger" loading={busy} onClick={onConfirm}>Odepnij</Button>
        </div>
      </div>
    </Modal>
  );
}

function UploadForm({
  connectionId, folderSlug, onDone,
}: {
  connectionId: string; folderSlug: string; onDone: () => void;
}) {
  const m = useConnectionFileMutations(connectionId);
  const [files, setFiles] = useState<File[]>([]);
  const [descriptions, setDescriptions] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFilesChange = (next: File[]) => {
    setFiles(next);
    setDescriptions((prev) => {
      const d = [...prev];
      while (d.length < next.length) d.push("");
      return d.slice(0, next.length);
    });
  };

  const reset = () => { setFiles([]); setDescriptions([]); setError(null); onDone(); };

  const upload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      await Promise.all(
        files.map((file, i) =>
          m.upload.mutateAsync({ file, description: descriptions[i] ?? "", folderSlug }),
        ),
      );
      reset();
    } catch {
      setError("Nie udało się przesłać niektórych plików.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card p-4 space-y-4 border-[var(--color-primary)]/30">
      <div>
        <label className="section-label block mb-1.5">Pliki *</label>
        <FileDropZone multiple files={files} onFilesChange={handleFilesChange} />
      </div>

      {files.length > 0 && (
        <div className="space-y-3">
          {files.map((file, i) => (
            <div key={i} className="flex items-start gap-3 p-2.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border-muted)]">
              <Paperclip size={13} className="text-[var(--color-text-muted)] mt-2 shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <p className="text-xs font-medium text-[var(--color-text-primary)] truncate">{file.name}</p>
                <input
                  className="input-base text-xs py-1 w-full"
                  placeholder="Opis (opcjonalnie)"
                  value={descriptions[i] ?? ""}
                  onChange={(e) => setDescriptions((prev) => prev.map((d, j) => (j === i ? e.target.value : d)))}
                />
              </div>
              <button
                type="button"
                className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors shrink-0 mt-1"
                onClick={() => handleFilesChange(files.filter((_, j) => j !== i))}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}

      <div className="flex gap-2">
        <Button size="sm" disabled={files.length === 0} loading={uploading} onClick={upload}>
          Prześlij {files.length > 1 ? `(${files.length})` : ""}
        </Button>
        <Button size="sm" variant="ghost" onClick={reset}>Anuluj</Button>
      </div>
    </div>
  );
}

// ── okno udostępniania w zleceniu (wszystko / folder / pojedynczy plik) ───────
// Jedno miejsce na cały cykl: co widzą dziś zamówienia, dołożenie kolejnego i cofnięcie —
// żeby nie trzeba było klikać folder po folderze, żeby dać podwykonawcy komplet.
function ShareToOrderModal({
  connectionId, target, current, onClose,
}: {
  connectionId: string;
  target: ShareTarget;
  // Zamówienia, które DZIŚ widzą ten cel (dla „wszystkiego" — wszystkie udostępnienia projektu).
  current: SharedOrderRef[];
  onClose: () => void;
}) {
  const ordersQ = useShareableOrders(connectionId);
  const shareM = useShareToOrder(connectionId);
  const unshareM = useUnshareFromOrder(connectionId);
  const [orderId, setOrderId] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const isAll = target.kind === "all";
  const targetLabel =
    target.kind === "all"
      ? "wszystkie niepoufne foldery i pliki"
      : target.kind === "folder"
        ? `folder „${target.label}”`
        : `plik „${target.filename}”`;

  // Przy celu „wszystkie niepoufne" wiersz opisuje całe udostępnienie zamówienia, nie
  // powód dla jednego węzła — stąd osobna etykieta zamiast SHARE_MODE_LABEL.
  const modeLabel = (r: SharedOrderRef) =>
    isAll && r.mode !== "all" ? "wybrane foldery / pliki" : SHARE_MODE_LABEL[r.mode];
  const shared = new Set(current.map((r) => r.order_id));
  const options = (ordersQ.data?.orders ?? [])
    .filter((o) => !o.share_from_other_project)
    .map((o) => ({
      value: o.id,
      label:
        `${o.order_number} · ${o.contractor_name} · ${o.status_display}`
        + (shared.has(o.id)
          ? " · już to widzi"
          : o.share_mode === "all"
            ? " · ma już wszystkie niepoufne"
            : o.share_mode === "selected"
              ? " · ma już udostępnienia"
              : ""),
    }));

  const payload = (order: string) => ({
    order,
    scope: isAll ? ("all" as const) : undefined,
    folderSlug: target.kind === "folder" ? target.slug : undefined,
    managedFileId: target.kind === "file" ? target.managedFileId : undefined,
  });

  const submit = () => {
    setError("");
    setDone("");
    const number = ordersQ.data?.orders.find((o) => o.id === orderId)?.order_number ?? "";
    shareM.mutate(payload(orderId), {
      onSuccess: () => {
        setDone(`Udostępniono ${targetLabel} w zamówieniu ${number}. Podwykonawca i zlecający`
          + " zobaczą je w sekcji „Pliki z projektu ZE”.");
        setOrderId("");
      },
      onError: (e) => setError(apiErr(e, "Nie udało się udostępnić w zleceniu.")),
    });
  };

  const withdraw = (ref: SharedOrderRef) => {
    setError("");
    setDone("");
    unshareM.mutate(payload(ref.order_id), {
      onSuccess: () => setDone(`Cofnięto udostępnienie w zamówieniu ${ref.order_number}.`),
      onError: (e) => setError(apiErr(e, "Nie udało się cofnąć udostępnienia.")),
    });
  };

  return (
    <Modal open onClose={onClose} title="Udostępnij w zleceniu" size="sm">
      <div className="space-y-3">
        <p className="text-sm text-[var(--color-text-secondary)]">
          Udostępniasz {targetLabel}. Udostępnienie to żywy podgląd (bez kopii plików) —
          poufne nigdy się w nim nie pojawiają.
        </p>

        {isAll && (
          <div className="border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11px] leading-snug text-[var(--color-text-secondary)] space-y-1">
            <p className="flex items-start gap-1.5 font-medium text-amber-700 dark:text-amber-400">
              <ShieldAlert size={13} className="mt-px shrink-0" />
              Reguła działa w przód
            </p>
            <p>
              Każdy plik dołożony później do dowolnego jawnego folderu też trafi do tego
              zamówienia — bez pytania. Żeby coś z niego wyjąć, oznacz to jako poufne albo
              cofnij całe udostępnienie.
            </p>
            <p>
              Foldery zastrzeżone na stałe (UMOWA PODPISANA, KORESPONDENCJA) nie wychodzą
              nigdy.
            </p>
          </div>
        )}

        {current.length > 0 && (
          <div className="space-y-1">
            <p className="section-label">Widzą to dziś</p>
            <ul className="divide-y divide-[var(--color-border-muted)] border border-[var(--color-border-muted)]">
              {current.map((r) => (
                <li key={r.order_id} className="flex items-center gap-2 px-2 py-1.5">
                  <span className="min-w-0 flex-1 text-xs">
                    <span className="font-medium text-[var(--color-text-primary)]">
                      {r.order_number}
                    </span>
                    {r.contractor_name && (
                      <span className="text-[var(--color-text-secondary)]"> · {r.contractor_name}</span>
                    )}
                    <span className="block text-[10px] text-[var(--color-text-muted)]">
                      {modeLabel(r)}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-40 transition-colors"
                    title={isAll
                      ? `Cofnij CAŁE udostępnienie plików projektu w zamówieniu ${r.order_number}`
                      : `Cofnij udostępnienie w zamówieniu ${r.order_number}`}
                    disabled={unshareM.isPending}
                    onClick={() => withdraw(r)}
                  >
                    <X size={12} /> Cofnij
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {ordersQ.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
            <Spinner size="sm" /> Ładowanie zamówień…
          </p>
        ) : options.length === 0 ? (
          <p className="text-xs text-amber-600">
            Brak zamówień powiązanych z tym przedsięwzięciem.
          </p>
        ) : (
          <SearchableSelect
            options={options}
            value={orderId}
            placeholder="— wybierz zamówienie —"
            onChange={(e) => setOrderId(e.target.value)}
          />
        )}

        {done && <p className="text-xs text-green-600 dark:text-green-400">{done}</p>}
        {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}

        <div className="flex gap-2">
          <Button
            className="flex-1"
            disabled={!orderId}
            loading={shareM.isPending}
            onClick={submit}
          >
            <Share2 size={13} className="mr-1.5" />Udostępnij
          </Button>
          <Button variant="ghost" onClick={onClose}>Zamknij</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── panel „Udostępnienia" — stan projektu + jeden ruch na komplet plików ──────
function SharesPanel({
  shares, onShareAll, onWithdraw, busy,
}: {
  shares: ProjectFileShareRow[];
  onShareAll: () => void;
  onWithdraw: (row: ProjectFileShareRow) => void;
  busy: boolean;
}) {
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="section-label">Udostępnienia plików w zamówieniach</p>
          <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
            {shares.length === 0
              ? "Żadne zamówienie nie widzi dziś plików tego projektu."
              : "Żywy podgląd — bez kopii plików. Poufne nigdy nie wychodzą."}
          </p>
        </div>
        <Button size="sm" className="shrink-0" disabled={busy} onClick={onShareAll}>
          <Share2 size={13} className="mr-1.5" />Udostępnij wszystkie niepoufne
        </Button>
      </div>
      {shares.length > 0 && (
        <ul className="divide-y divide-[var(--color-border-muted)] border border-[var(--color-border-muted)]">
          {shares.map((r) => (
            <li key={r.order_id} className="flex items-center gap-2 px-2 py-1.5">
              <span className="min-w-0 flex-1 text-xs">
                <span className="font-medium text-[var(--color-text-primary)]">{r.order_number}</span>
                {r.contractor_name && (
                  <span className="text-[var(--color-text-secondary)]"> · {r.contractor_name}</span>
                )}
                <span className="text-[var(--color-text-muted)]"> · {r.status_display}</span>
                <span className="block text-[10px] text-[var(--color-text-muted)]">
                  {r.mode === "all"
                    ? "wszystkie niepoufne (także pliki dołożone później)"
                    : `wybrane: ${r.folder_count} ${r.folder_count === 1 ? "folder" : "folderów"}`
                      + (r.file_count > 0
                        ? `, ${r.file_count} ${r.file_count === 1 ? "plik" : "plików"} pojedynczo`
                        : "")}
                  {" · "}
                  {r.shared_file_count} {r.shared_file_count === 1 ? "plik" : "plików"} widocznych
                </span>
              </span>
              <button
                type="button"
                className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-40 transition-colors"
                title={`Cofnij całe udostępnienie w zamówieniu ${r.order_number}`}
                disabled={busy}
                onClick={() => onWithdraw(r)}
              >
                <X size={12} /> Cofnij
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── modal „kopiuj / przenieś do folderu" ──────────────────────────────────────
function TransferModal({
  connectionId, target, folders, onClose,
}: {
  connectionId: string; target: TransferTarget; folders: FolderNode[]; onClose: () => void;
}) {
  const transferM = useTransferProjectFiles(connectionId);
  const [destSlug, setDestSlug] = useState("");
  const [error, setError] = useState("");
  const [doneCount, setDoneCount] = useState<number | null>(null);

  const { operation, source } = target;
  const excludeSlug = source.kind === "file" ? source.fromSlug : source.slug;
  // Bez folderów zastrzeżonych na stałe (KORESPONDENCJA) — do nich nie wolno przenosić
  // ani kopiować (backend odrzuca taki cel).
  const options = manualFolders(folders)
    .filter((f) => f.slug !== excludeSlug && !f.confidential_locked)
    .map((f) => ({ value: f.slug, label: `${f.label} (${f.file_count})` }));
  const isCopy = operation === "copy";
  const sourceLabel =
    source.kind === "file" ? `plik „${source.filename}"` : `zawartość folderu „${source.label}"`;

  const submit = () => {
    setError("");
    transferM.mutate(
      {
        operation,
        target_slug: destSlug,
        managed_file_id:
          source.kind === "file" && isCopy ? source.managedFileId : undefined,
        connection_file_id:
          source.kind === "file" && !isCopy ? source.connectionFileId ?? undefined : undefined,
        source_slug: source.kind === "folder" ? source.slug : undefined,
      },
      {
        onSuccess: (tree) => setDoneCount(tree.transferred ?? 0),
        onError: (e) =>
          setError(apiErr(e, isCopy ? "Nie udało się skopiować." : "Nie udało się przenieść.")),
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={isCopy ? "Kopiuj do folderu" : "Przenieś do folderu"} size="sm">
      {doneCount != null ? (
        <div className="space-y-3">
          <p className="text-sm text-green-600 dark:text-green-400">
            {isCopy ? "Skopiowano" : "Przeniesiono"} {doneCount}{" "}
            {doneCount === 1 ? "plik" : doneCount < 5 ? "pliki" : "plików"}.
          </p>
          <Button className="w-full" onClick={onClose}>Zamknij</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-text-secondary)]">
            {isCopy ? "Kopiujesz" : "Przenosisz"} {sourceLabel}. Docelowy może być tylko folder
            nieautomatyczny{isCopy ? " (kopia to referencja — bez powielania pliku)" : ""}.
          </p>
          {options.length === 0 ? (
            <p className="text-xs text-amber-600">Brak dostępnych folderów docelowych.</p>
          ) : (
            <SearchableSelect
              options={options}
              value={destSlug}
              placeholder="— wybierz folder docelowy —"
              onChange={(e) => setDestSlug(e.target.value)}
            />
          )}
          {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}
          <div className="flex gap-2">
            <Button className="flex-1" disabled={!destSlug} loading={transferM.isPending} onClick={submit}>
              {isCopy ? <Copy size={13} className="mr-1.5" /> : <FolderInput size={13} className="mr-1.5" />}
              {isCopy ? "Kopiuj" : "Przenieś"}
            </Button>
            <Button variant="ghost" onClick={onClose}>Anuluj</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export function FilesTab({ connection }: { connection: PowerProject }) {
  const { data, isLoading } = useProjectFolders(connection.id);
  const qc = useQueryClient();
  // Edytor w przeglądarce (Collabora): formaty z discovery serwera; bez edytora — tylko pobieranie.
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  const [editorEntry, setEditorEntry] = useState<FolderFileEntry | null>(null);
  // Podgląd zdjęć jak w GG — seria obrazów wybranego folderu.
  const [lightbox, setLightbox] = useState<ImageSeries | null>(null);
  const m = useConnectionFileMutations(connection.id);
  const confM = useSetFolderConfidentiality(connection.id);
  const createFolderM = useCreateProjectFolder(connection.id);
  const renameFolderM = useRenameProjectFolder(connection.id);
  const deleteFolderM = useDeleteProjectFolder(connection.id);
  const unshareM = useUnshareFromOrder(connection.id);
  const detachWypisM = useDetachWypisFromFolder(connection.id);
  const [detachTarget, setDetachTarget] = useState<FolderFileEntry | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [transferTarget, setTransferTarget] = useState<TransferTarget | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderLabel, setNewFolderLabel] = useState("");
  const [createError, setCreateError] = useState("");
  // Zmiana nazwy folderu własnego — panel inline nad drzewem (jak tworzenie).
  const [renamingFolder, setRenamingFolder] = useState<{ slug: string; label: string } | null>(null);
  const [renameError, setRenameError] = useState("");

  const submitNewFolder = () => {
    setCreateError("");
    createFolderM.mutate(newFolderLabel, {
      onSuccess: () => { setCreatingFolder(false); setNewFolderLabel(""); },
      onError: (e) => setCreateError(apiErr(e, "Nie udało się utworzyć folderu.")),
    });
  };

  const submitRenameFolder = () => {
    if (!renamingFolder) return;
    setRenameError("");
    renameFolderM.mutate(renamingFolder, {
      onSuccess: () => setRenamingFolder(null),
      onError: (e) => setRenameError(apiErr(e, "Nie udało się zmienić nazwy folderu.")),
    });
  };

  const onDeleteFolder = (node: FolderNode) => {
    const suffix = node.file_count > 0
      ? ` ${node.file_count === 1 ? "Plik trafi" : `Pliki (${node.file_count}) trafią`} do „Inne / bez folderu".`
      : "";
    if (!confirm(`Usunąć folder „${node.label}"?${suffix}`)) return;
    deleteFolderM.mutate(node.slug, {
      onSuccess: () => {
        if (selectedSlug === node.slug) setSelectedSlug("manual_inne");
      },
      onError: (e) => alert(apiErr(e, "Nie udało się usunąć folderu.")),
    });
  };

  // ZIP download of the whole tree (variables: null) or a single folder subtree (slug).
  const zipM = useMutation({
    mutationFn: (folderSlug: string | null) =>
      downloadProjectFolderZip(connection.id, folderSlug ?? undefined),
  });

  const folders = data?.folders ?? [];
  const totalFiles = folders.reduce((sum, f) => sum + f.file_count, 0);
  const canShareToOrder = data?.can_share_to_order ?? false;
  const shares = data?.shares ?? [];

  // Kto DZIŚ widzi cel otwartego okna udostępniania. Liczone z tych samych danych, które
  // rysują znaczniki w drzewie, więc po udostępnieniu/cofnięciu lista odświeża się sama.
  const shareCurrent = useMemo<SharedOrderRef[]>(() => {
    if (!shareTarget) return [];
    if (shareTarget.kind === "all") {
      return shares.map((r) => ({
        order_id: r.order_id,
        order_number: r.order_number,
        contractor_name: r.contractor_name,
        mode: r.mode === "all" ? "all" : "folder",
      }));
    }
    if (shareTarget.kind === "folder") {
      return findNode(folders, shareTarget.slug)?.shared_orders ?? [];
    }
    return findEntry(folders, shareTarget.managedFileId)?.shared_orders ?? [];
  }, [shareTarget, folders, shares]);

  const withdrawWholeShare = (row: ProjectFileShareRow) => {
    if (!confirm(
      `Cofnąć całe udostępnienie plików projektu w zamówieniu ${row.order_number}`
      + `${row.contractor_name ? ` (${row.contractor_name})` : ""}?`
      + " Podwykonawca straci sekcję „Pliki z projektu ZE”.",
    )) return;
    unshareM.mutate(
      { order: row.order_id, scope: "all" },
      { onError: (e) => alert(apiErr(e, "Nie udało się cofnąć udostępnienia.")) },
    );
  };

  // Default selection: first leaf that has files, else the first leaf overall.
  const selected = useMemo(() => {
    if (!folders.length) return null;
    const leaves = collectLeaves(folders);
    if (selectedSlug) return findNode(folders, selectedSlug);
    return leaves.find((l) => l.file_count > 0) ?? leaves[0] ?? null;
  }, [folders, selectedSlug]);

  const onSelect = (slug: string) => { setSelectedSlug(slug); setAdding(false); };

  if (isLoading) {
    return <div className="flex justify-center py-10 mt-4"><Spinner /></div>;
  }

  // Upload allowed in any writable leaf: manual and auto folders alike (a manual file sits
  // next to the projection from the source module). Backend keeps read_only only for
  // containers and KORESPONDENCJA.
  const canUpload = selected != null && !selected.read_only;

  return (
    <div className="mt-4 space-y-4">
      {/* Stan udostępnień projektu + „wszystkie niepoufne" jednym ruchem — dla kierownika
          przedsięwzięcia i dyrekcji; reszta zespołu nie dostaje tych danych z backendu. */}
      {canShareToOrder && (
        <SharesPanel
          shares={shares}
          busy={unshareM.isPending}
          onShareAll={() => setShareTarget({ kind: "all" })}
          onWithdraw={withdrawWholeShare}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] gap-4">
      {/* left: folder tree */}
      <aside className="card overflow-hidden self-start">
        <div className="py-2 px-3 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] flex items-center justify-between gap-2">
          <p className="section-label">Foldery</p>
          <div className="flex items-center gap-2">
            {(data?.can_create_folder ?? false) && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
                title="Utwórz nowy folder własny projektu"
                onClick={() => { setCreatingFolder((v) => !v); setCreateError(""); }}
              >
                <FolderPlus size={14} />
              </button>
            )}
            {totalFiles > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] disabled:opacity-60 transition-colors"
                title="Pobierz wszystkie pliki ze strukturą folderów jako ZIP"
                disabled={zipM.isPending}
                onClick={() => zipM.mutate(null)}
              >
                {zipM.isPending && zipM.variables == null ? <Spinner size="sm" /> : <FolderArchive size={14} />}
                ZIP
              </button>
            )}
          </div>
        </div>
        {creatingFolder && (
          <div className="p-2 border-b border-[var(--color-border-muted)] space-y-1.5">
            <input
              className="input-base text-xs w-full"
              placeholder="Nazwa nowego folderu"
              value={newFolderLabel}
              autoFocus
              maxLength={80}
              onChange={(e) => setNewFolderLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newFolderLabel.trim()) submitNewFolder();
                if (e.key === "Escape") setCreatingFolder(false);
              }}
            />
            {createError && <p className="text-[11px] text-[var(--color-primary)] font-mono">{createError}</p>}
            <div className="flex gap-1.5">
              <Button
                size="sm"
                disabled={!newFolderLabel.trim()}
                loading={createFolderM.isPending}
                onClick={submitNewFolder}
              >
                Utwórz
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCreatingFolder(false)}>Anuluj</Button>
            </div>
          </div>
        )}
        {renamingFolder && (
          <div className="p-2 border-b border-[var(--color-border-muted)] space-y-1.5">
            <input
              className="input-base text-xs w-full"
              placeholder="Nowa nazwa folderu"
              value={renamingFolder.label}
              autoFocus
              maxLength={80}
              onChange={(e) => setRenamingFolder({ ...renamingFolder, label: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter" && renamingFolder.label.trim()) submitRenameFolder();
                if (e.key === "Escape") setRenamingFolder(null);
              }}
            />
            {renameError && <p className="text-[11px] text-[var(--color-primary)] font-mono">{renameError}</p>}
            <div className="flex gap-1.5">
              <Button
                size="sm"
                disabled={!renamingFolder.label.trim()}
                loading={renameFolderM.isPending}
                onClick={submitRenameFolder}
              >
                Zmień nazwę
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenamingFolder(null)}>Anuluj</Button>
            </div>
          </div>
        )}
        <div className="py-1">
          {folders.map((n) => (
            <TreeNode
              key={n.slug}
              node={n}
              depth={0}
              selectedSlug={selected?.slug ?? ""}
              onSelect={onSelect}
              onDownloadZip={(slug) => zipM.mutate(slug)}
              zipBusy={zipM.isPending}
              canManageConfidential={data?.can_manage_confidential ?? false}
              onToggleConfidential={(folderSlug, isConfidential) =>
                confM.mutate({ folderSlug, isConfidential })
              }
              confidentialBusy={confM.isPending}
              canShareToOrder={canShareToOrder}
              onShareToOrder={setShareTarget}
              canManageFolders={data?.can_create_folder ?? false}
              onRenameFolder={(node) => {
                setRenameError("");
                setRenamingFolder({ slug: node.slug, label: node.label });
              }}
              onDeleteFolder={onDeleteFolder}
            />
          ))}
        </div>
      </aside>

      {/* right: selected folder contents */}
      <section className="space-y-4 min-w-0">
        {selected && (
          // flex-wrap: pasek akcji urósł (udostępnianie + kopiuj/przenieś + ZIP + upload)
          // i przy wąskim oknie nachodził na nazwę folderu — teraz spada do drugiej linii.
          <div className="flex items-start justify-between gap-3 flex-wrap">
            {/* Nazwa folderu ma własne minimum — inaczej rosnący pasek akcji ściskał ją do
                dwóch liter zamiast zejść do drugiej linii. */}
            <div className="flex-1 min-w-[220px]">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-1.5">
                {selected.label}
                {selected.read_only && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-normal text-[var(--color-text-muted)]">
                    <Lock size={11} /> tylko do odczytu
                  </span>
                )}
                {selected.is_confidential && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-normal ${
                      selected.confidential_locked ? "text-[var(--color-primary)]" : "text-amber-600"
                    }`}
                    title={
                      selected.confidential_locked
                        ? selected.move_out_allowed
                          ? "Folder zastrzeżony na stałe — widzą go wyłącznie kierownicy, dyrektorzy i CEO; nie podlega udostępnieniu. Pliki można przenieść do innego folderu — pozostają wtedy poufne do jawnego odpoufnienia."
                          : "Folder zastrzeżony na stałe — widzą go wyłącznie kierownicy, dyrektorzy i CEO. Poufności nie można zdjąć, folder nie podlega udostępnieniu, a plików nie da się z niego wynieść."
                        : "Widoczny tylko dla koordynatora, dyrektora i CEO (obejmuje podfoldery). Nie podlega udostępnieniu podwykonawcom."
                    }
                  >
                    <ShieldAlert size={11} /> {selected.confidential_locked ? "zastrzeżony na stałe" : "poufny"}
                  </span>
                )}
              </h3>
              {selected.source_hint && (
                <p className="text-xs text-[var(--color-text-muted)] mt-0.5 flex items-start gap-1">
                  <Info size={12} className="mt-0.5 shrink-0" />
                  <span>{selected.source_hint}</span>
                </p>
              )}
              {/* Źródło tego folderu padło — pozostałe foldery działają. Bez tego
                  komunikatu pusty folder wyglądałby jak „nie ma tu plików”. */}
              {selected.source_error && (
                <p className="text-xs text-amber-700 mt-0.5 flex items-start gap-1">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <span>{selected.source_error}</span>
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 shrink-0 flex-wrap">
              {canShareToOrder && !selected.is_confidential && (
                <Button
                  size="sm"
                  variant="ghost"
                  title={
                    (selected.shared_orders ?? []).length > 0
                      ? shareTitle(selected.shared_orders ?? [])
                      : "Udostępnij ten folder w zamówieniu podwykonawcy"
                  }
                  onClick={() =>
                    setShareTarget({ kind: "folder", slug: selected.slug, label: selected.label })
                  }
                >
                  <Share2 size={13} className="mr-1.5" />
                  {(selected.shared_orders ?? []).length > 0
                    ? `Udostępniony (${(selected.shared_orders ?? []).length})`
                    : "Udostępnij w zleceniu"}
                </Button>
              )}
              {selected.entries.length > 0 && !selected.confidential_locked && (
                <Button
                  size="sm"
                  variant="ghost"
                  title="Kopiuj całą zawartość folderu do folderu nieautomatycznego"
                  onClick={() =>
                    setTransferTarget({
                      operation: "copy",
                      source: { kind: "folder", slug: selected.slug, label: selected.label },
                    })
                  }
                >
                  <Copy size={13} className="mr-1.5" />Kopiuj do…
                </Button>
              )}
              {selected.kind === "manual" && selected.entries.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  title="Przenieś całą zawartość folderu do innego folderu nieautomatycznego"
                  onClick={() =>
                    setTransferTarget({
                      operation: "move",
                      source: { kind: "folder", slug: selected.slug, label: selected.label },
                    })
                  }
                >
                  <FolderInput size={13} className="mr-1.5" />Przenieś do…
                </Button>
              )}
              {selected.file_count > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={zipM.isPending && zipM.variables === selected.slug}
                  disabled={zipM.isPending}
                  onClick={() => zipM.mutate(selected.slug)}
                >
                  <Download size={13} className="mr-1.5" />Pobierz ZIP
                </Button>
              )}
              {canUpload && !adding && (
                <Button size="sm" onClick={() => setAdding(true)}>
                  <Upload size={13} className="mr-1.5" />Dodaj plik
                </Button>
              )}
            </div>
          </div>
        )}

        {canUpload && adding && selected && (
          <UploadForm connectionId={connection.id} folderSlug={selected.slug} onDone={() => setAdding(false)} />
        )}

        {selected && selected.entries.length === 0 && (
          <div className="card p-8 text-center">
            <Paperclip size={24} className="mx-auto mb-2 text-[var(--color-text-muted)]" />
            <p className="text-sm text-[var(--color-text-muted)]">
              {!selected.read_only ? "Brak plików w tym folderze." : "Ten folder jest jeszcze pusty."}
            </p>
          </div>
        )}

        {selected && selected.entries.length > 0 && (
          <div className="card overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
                  <th className="py-2 pl-4 pr-2 w-6" />
                  <th className="py-2 pr-4 section-label">Nazwa / opis</th>
                  <th className="py-2 pr-4 section-label whitespace-nowrap">Rozmiar</th>
                  <th className="py-2 pr-4 section-label whitespace-nowrap">Data</th>
                  <th className="py-2 pr-3 w-16" />
                </tr>
              </thead>
              <tbody>
                {selected.entries.map((entry) => {
                  // Zdjęcia (jpg/png…) → podgląd jak w GG; reszta wg discovery Collabory.
                  const action = filesTabOpenAction(entry.filename, editorExt);
                  const openAction: FilesTabOpenAction | undefined = action === "image"
                    ? (entry.url ? "image" : undefined)
                    : action
                      ? (action === "edit" && canEditContent(entry) ? "edit" : "view")
                      : undefined;
                  const entriesHere = selected.entries;
                  return (
                  <EntryRow
                    key={`${entry.managed_file_id}-${entry.source_ref.id}`}
                    entry={entry}
                    saving={m.update.isPending}
                    openAction={openAction}
                    onOpen={
                      openAction === "image"
                        ? () => setLightbox(imageSeries(entriesHere, entry))
                        : openAction ? () => setEditorEntry(entry) : undefined
                    }
                    onSaveDescription={(description, close) =>
                      entry.connection_file_id &&
                      m.update.mutate({ id: entry.connection_file_id, description }, { onSuccess: close })
                    }
                    savingName={m.rename.isPending}
                    onSaveFilename={(filename, close) =>
                      entry.connection_file_id &&
                      m.rename.mutate({ id: entry.connection_file_id, filename }, { onSuccess: close })
                    }
                    onDelete={() => {
                      if (entry.connection_file_id && confirm(`Usunąć plik „${entry.filename}"?`)) {
                        m.remove.mutate(entry.connection_file_id);
                      }
                    }}
                    onDetachWypis={
                      (data?.can_detach_wypis ?? false) && entry.source_ref.kind === "parcel_wypis"
                        ? () => setDetachTarget(entry)
                        : undefined
                    }
                    onShareToOrder={
                      canShareToOrder && !entry.is_confidential
                        ? () => setShareTarget({
                            kind: "file",
                            managedFileId: entry.managed_file_id,
                            filename: entry.filename,
                          })
                        : undefined
                    }
                    onToggleConfidential={
                      (data?.can_manage_confidential ?? false)
                        ? (isConfidential) =>
                            confM.mutate({ managedFileId: entry.managed_file_id, isConfidential })
                        : undefined
                    }
                    onCopy={
                      entry.confidential_locked
                        ? undefined // z folderu zastrzeżonego na stałe nic nie wynosimy
                        : () =>
                            setTransferTarget({
                              operation: "copy",
                              source: {
                                kind: "file",
                                filename: entry.filename,
                                fromSlug: selected.slug,
                                managedFileId: entry.managed_file_id,
                                connectionFileId: entry.connection_file_id,
                              },
                            })
                    }
                    onMove={
                      selected.kind === "manual" && entry.connection_file_id
                        // Z folderu zastrzeżonego wolno przenieść tylko przy move_out_allowed
                        // (KORESPONDENCJA) — plik zachowuje wtedy jawną flagę poufności.
                        && (!entry.confidential_locked || selected.move_out_allowed)
                        ? () =>
                            setTransferTarget({
                              operation: "move",
                              source: {
                                kind: "file",
                                filename: entry.filename,
                                fromSlug: selected.slug,
                                managedFileId: entry.managed_file_id,
                                connectionFileId: entry.connection_file_id,
                              },
                            })
                        : undefined
                    }
                  />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </div>

      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          index={lightbox.index}
          onIndexChange={(index) => setLightbox({ ...lightbox, index })}
          onClose={() => setLightbox(null)}
        />
      )}
      {editorEntry && (
        <OdtEditorScreen
          kind="project_file"
          id={editorSlotId(connection.id, editorEntry)}
          readOnly={!canEditContent(editorEntry)}
          onClose={(saved) => {
            setEditorEntry(null);
            // Zapis podmienia plik (rozmiar, data) — odświeżamy drzewo i płaską listę.
            if (saved) {
              qc.invalidateQueries({ queryKey: qk.projectFolders(connection.id) });
              qc.invalidateQueries({ queryKey: qk.connectionFiles(connection.id) });
            }
          }}
        />
      )}

      {detachTarget && (
        <DetachWypisModal
          entry={detachTarget}
          busy={detachWypisM.isPending}
          onClose={() => setDetachTarget(null)}
          onConfirm={() =>
            detachWypisM.mutate(detachTarget.source_ref.id, {
              onSuccess: () => setDetachTarget(null),
              onError: (e) => alert(apiErr(e, "Nie udało się odpiąć wypisu.")),
            })
          }
        />
      )}
      {shareTarget && (
        <ShareToOrderModal
          connectionId={connection.id}
          target={shareTarget}
          current={shareCurrent}
          onClose={() => setShareTarget(null)}
        />
      )}

      {transferTarget && (
        <TransferModal
          connectionId={connection.id}
          target={transferTarget}
          folders={folders}
          onClose={() => setTransferTarget(null)}
        />
      )}
    </div>
  );
}
